import { apiGet } from './client';
import type { OperationResult } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Prebuilt prompt history — append-only audit trail of body changes
// ─────────────────────────────────────────────────────────────────────────────
//
// Pairs with backend ai.PrebuiltPromptHistory and the PrebuiltPromptController
// History endpoints. Every change to ai.PrebuiltPrompt.Instructions writes a
// row through PrebuiltPromptHistoryManager.AppendAsync — manual edits via the
// Edit modal, Review-with-AI Accept / Edit, and import paths.
//
// The list endpoint omits the bodies (each can be 30 KB); the detail
// endpoint returns the full prior + new body for a side-by-side diff.
// ─────────────────────────────────────────────────────────────────────────────

/** "manual_edit" | "review_accept" | "review_edit" | "import" */
export type PrebuiltPromptChangeSource =
  | 'manual_edit'
  | 'review_accept'
  | 'review_edit'
  | 'import';

export interface PrebuiltPromptHistoryRow {
  id: number;
  prebuiltPromptId: number;
  changedAtUtc: string;
  changedBy: number;
  /** Resolved name of the editor; null when the user no longer exists. */
  changedByName: string | null;
  changeSource: PrebuiltPromptChangeSource;
  reviewResultId: number | null;
  previousBodyHash: string | null;
  /** Cached length of the new body — visible delta = new - previous. */
  newLength: number;
  /** Cached length of the prior body. */
  previousLength: number;
  note: string | null;
}

export interface PrebuiltPromptHistoryDetail extends PrebuiltPromptHistoryRow {
  /** Full prior body — renderable in the diff view. */
  previousInstructions: string | null;
  /** Full new body — what was written by this change. */
  newInstructions: string | null;
}

/**
 * List the audit trail for one prompt, latest first. Bodies omitted to
 * keep the response payload small; use {@link getHistoryDetail} for the
 * full content of a specific row.
 */
export async function listHistory(
  promptId: number,
  take: number = 50,
): Promise<PrebuiltPromptHistoryRow[]> {
  try {
    const wrapped = await apiGet<OperationResult<PrebuiltPromptHistoryRow[]>>(
      `/api/PrebuiltPrompt/History/${promptId}?take=${take}`,
    );
    if (!wrapped.isSuccess || !wrapped.data) return [];
    return wrapped.data;
  } catch {
    return [];
  }
}

/**
 * Load both bodies for a single history row. Used to render the
 * before/after diff view in the timeline drawer.
 */
export async function getHistoryDetail(
  promptId: number,
  historyId: number,
): Promise<PrebuiltPromptHistoryDetail | null> {
  try {
    const wrapped = await apiGet<OperationResult<PrebuiltPromptHistoryDetail>>(
      `/api/PrebuiltPrompt/History/${promptId}/${historyId}`,
    );
    if (!wrapped.isSuccess || !wrapped.data) return null;
    return wrapped.data;
  } catch {
    return null;
  }
}

/** Plain-language label for each ChangeSource value, used in the UI. */
export const CHANGE_SOURCE_LABEL: Record<PrebuiltPromptChangeSource, string> = {
  manual_edit: 'Manual edit',
  review_accept: 'AI accepted',
  review_edit: 'AI edited',
  import: 'Imported',
};
